/* ==========================================================================
   CLARENDALE — the small amount of behaviour the ported sections need.

   Everything else is Impulse's: image loading, button hover and press, the
   PhotoSwipe product zoom, the cart drawer itself. This file only covers the
   two things Impulse has no equivalent for — swapping the low-stock line when
   the variant changes, and the milestone ladder.
   ========================================================================== */
(function () {
  "use strict";

  /* ------------------------------------------------------------------------
     Low stock line follows the variant picker.

     Impulse dispatches TWO events from the same handler: 'variantChange' on
     the product CONTAINER — without bubbles:true, so it never reaches a
     document-level listener — and 'variant:change' on document itself. This
     file listened for the first one at document level, which is why the line
     and the dots were frozen on the first variant: the handler never ran
     after page load. 'variant:change' is the one that actually arrives.
     Both are bound so a bubbling variantChange from anything else still works.

     Every variant's line is already in the DOM, so this is a show/hide rather
     than a re-render.
     ------------------------------------------------------------------------ */
  function onVariantChange(evt) {
    var scope = evt.target && evt.target.closest ? evt.target.closest("[data-section-id]") || document : document;
    var holder = scope.querySelector("[data-cl-stock]");
    var variant = evt.detail && evt.detail.variant;

    if (holder) {
      var lines = holder.querySelectorAll("[data-cl-stock-for]");
      for (var i = 0; i < lines.length; i++) {
        var match = variant && String(lines[i].dataset.clStockFor) === String(variant.id);
        lines[i].hidden = !match;
      }
    }
    markSizes(scope, variant);
  }

  document.addEventListener("variantChange", onVariantChange);
  document.addEventListener("variant:change", onVariantChange);

  /* ------------------------------------------------------------------------
     Stock dots on the size chips.

     Impulse's variant picker knows availability but not depth, so the level
     comes from the inventory map the stock block prints. The dots are scoped to
     the colour that is currently selected — "only 2 left in S" is a fact about
     one colourway, not about the size run.
     ------------------------------------------------------------------------ */
  /* How many size chips may carry the red "running out" dot at once. */
  var MAX_LOW_DOTS = 3;

  function inventoryMap(scope) {
    var el = (scope || document).querySelector("[data-cl-inventory]");
    if (!el) return null;
    if (el._parsed) return el._parsed;
    try { el._parsed = JSON.parse(el.textContent); } catch (e) { el._parsed = null; }
    return el._parsed;
  }

  function markSizes(scope, variant) {
    scope = scope || document;
    var map = inventoryMap(scope);
    if (!map || !map.variants) return;

    /* which option is the size? the one that is not the colour */
    var sizeIndex = map.sizeIndex;
    if (sizeIndex == null) return;

    var current = variant || map.variants.filter(function (v) { return v.id === map.currentId; })[0];
    var otherOpts = (current && current.options) || [];

    var labels = scope.querySelectorAll(".variant-input-wrap label.variant__button-label:not(.color-swatch)");
    var lowCandidates = [];
    for (var i = 0; i < labels.length; i++) {
      var label = labels[i];
      var value = (label.textContent || "").trim();
      label.removeAttribute("data-cl-level");
      var halo = label.querySelector(".cl-dot-pulse");
      if (halo) halo.remove();

      /* the variant that is this size, in the colour she is looking at */
      var hit = null;
      for (var v = 0; v < map.variants.length; v++) {
        var cand = map.variants[v];
        if (cand.options[sizeIndex] !== value) continue;
        var sameOtherwise = true;
        for (var o = 0; o < cand.options.length; o++) {
          if (o !== sizeIndex && otherOpts[o] && cand.options[o] !== otherOpts[o]) sameOtherwise = false;
        }
        if (sameOtherwise) { hit = cand; break; }
      }
      if (!hit) continue;

      if (!hit.available) {
        /* Sold out is a fact about that size, not a scarcity claim, so every
           one of them keeps its grey dot — the cap below does not apply. */
        label.setAttribute("data-cl-level", "out");
      } else if (hit.tracked && hit.qty > 0 && hit.qty <= map.threshold) {
        lowCandidates.push({ label: label, qty: hit.qty });
      }
    }

    /* At most THREE red dots (buyer, 15 Aug). The display threshold went from 5
       to 8 in the previous round so single-variant products would still show a
       count, and the side effect landed here: on a six-size run almost every
       chip qualified, and a row where everything is running out says nothing is.

       The three KEPT are the three shallowest, because if the row is going to
       point at something it should point at the sizes actually going first.
       `sort` is stable in every browser that matters, so equal quantities keep
       their DOM order and the dots do not move between renders. */
    lowCandidates.sort(function (a, b) { return a.qty - b.qty; });
    for (var c = 0; c < lowCandidates.length && c < MAX_LOW_DOTS; c++) {
      var lbl = lowCandidates[c].label;
      lbl.setAttribute("data-cl-level", "low");
      var dot = document.createElement("span");
      dot.className = "cl-dot-pulse";
      dot.setAttribute("aria-hidden", "true");
      lbl.appendChild(dot);
    }
  }

  function initSizes() {
    var scopes = document.querySelectorAll("[data-cl-inventory]");
    for (var i = 0; i < scopes.length; i++) {
      markSizes(scopes[i].closest("[data-section-id]") || document, null);
    }
  }

  document.addEventListener("shopify:section:load", initSizes);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSizes);
  } else {
    initSizes();
  }

  /* ------------------------------------------------------------------------
     Size-chart table restyle.

     The chart is page content carried in .tool-tip-trigger__content, and its
     header cells arrive with INLINE `background-color: #2C2C2C !important` —
     an inline !important outranks every stylesheet, so this cannot be done in
     CSS. The inline styles are rewritten once at load, before theme.js copies
     the content into the opened tool-tip.

     Buyer 13 Aug round 6: white / neutral grey alternating rows with a
     hairline between them — the ORIGINAL theme's feel, because a borderless
     warm tint reads as a stain on a dense numeric table and with no rules at
     all the eye loses the row on a wide chart.

     Buyer 23 Aug: same structure, the shop's OWN palette instead of the
     original theme's greys. The six values below are the whole chart and they
     are the buyer's hex codes verbatim — the neutral ramp of §2 of the
     handoff, so the chart now sits on the same ladder as every panel on the
     site instead of on a white island.

     ⚠️ The header row is the one exception to "the text colour is #2a2724".
     Ink on stone is 1.3:1 and unreadable; the header keeps the paper on it.
     Everything the shopper actually reads — every data cell — is the ink.
     ------------------------------------------------------------------------ */
  var SC_HEAD     = "#443e39";   /* header row fill — the header/footer stone */
  var SC_HEAD_INK = "#faf8f6";   /* paper ON that stone; ink here is unreadable */
  var SC_ROW_A    = "#faf8f6";   /* odd data rows — the page ground */
  var SC_ROW_B    = "#f3f0ee";   /* even data rows — the panel */
  var SC_LINE     = "#ded9d6";   /* the hairline between rows */
  var SC_INK      = "#2a2724";   /* the text colour */

  /* ------------------------------------------------------------------------
     One blank line between the prose blocks, never more.

     The Fit / Sizing tip / Materials block is merchant-written page content and
     the spacing arrives however it was typed: a stray <br> between paragraphs on
     one product, an empty <p> on the next, `<h4><br></h4>` used as a spacer on a
     third. On a good product it reads as three paragraphs; on a bad one there
     are three blank lines between each and the chart looks broken.

     Two halves, and it needs both to be a hard lock:

       1. Here — delete the empties. A block with no text, no image and no table
          is a spacer someone typed, so it goes; runs of <br> collapse to one;
          a <br> at the very start or end of the block goes entirely.
       2. In the stylesheet (§33) — a fixed margin on the surviving paragraphs.
          Stripping alone is not enough, because two <p>s with their own margins
          still stack differently from a <p> and a <div>.

     Note it tests `textContent`, NOT innerHTML: `<p><br></p>` is not empty by
     the second measure and that is exactly the case being removed (§5, "<br> is
     not blank").
     ------------------------------------------------------------------------ */
  function tidyProse(root) {
    if (!root || root.dataset.clTidied) return;
    root.dataset.clTidied = "1";

    var blocks = root.querySelectorAll("p, div, h1, h2, h3, h4, h5, h6");
    for (var i = blocks.length - 1; i >= 0; i--) {
      var el = blocks[i];
      if (el.classList.contains("cl-size-note")) continue;
      if (el.querySelector("table, img, svg, input, select")) continue;
      if ((el.textContent || "").replace(/ /g, " ").trim() === "") el.remove();
    }

    /* collapse <br> runs, and drop them at either end of the block */
    var brs = root.querySelectorAll("br");
    for (var b = 0; b < brs.length; b++) {
      var br = brs[b];
      var prev = br.previousSibling;
      while (prev && prev.nodeType === 3 && !prev.textContent.trim()) prev = prev.previousSibling;
      var next = br.nextSibling;
      while (next && next.nodeType === 3 && !next.textContent.trim()) next = next.nextSibling;
      var prevIsBr = prev && prev.nodeType === 1 && prev.tagName === "BR";
      if (!prev || !next || prevIsBr) br.remove();
    }
  }

  function restyleSizeCharts() {
    var proseRoots = document.querySelectorAll(
      ".tool-tip-trigger__content, tool-tip[data-tool-tip] .tool-tip__content"
    );
    for (var q = 0; q < proseRoots.length; q++) tidyProse(proseRoots[q]);

    var tables = document.querySelectorAll(
      ".tool-tip-trigger__content table, tool-tip[data-tool-tip] table"
    );
    for (var t = 0; t < tables.length; t++) {
      var table = tables[t];
      if (table.dataset.clRestyled) continue;
      table.dataset.clRestyled = "1";

      var cells = table.querySelectorAll("th, td");
      for (var c = 0; c < cells.length; c++) {
        cells[c].style.setProperty("border", "0", "important");
        cells[c].style.setProperty("border-bottom", "1px solid " + SC_LINE, "important");
        cells[c].style.setProperty("color", SC_INK, "important");
      }

      var ths = table.querySelectorAll("th");
      for (var h = 0; h < ths.length; h++) {
        ths[h].style.setProperty("background-color", SC_HEAD, "important");
        ths[h].style.setProperty("color", SC_HEAD_INK, "important");
        ths[h].style.setProperty("border-bottom", "0", "important");
      }

      /* zebra over the DATA rows only (a row is data when it holds a td), so
         the count is right whether or not the chart uses a thead */
      var rows = table.querySelectorAll("tr");
      var dataIndex = 0;
      for (var r = 0; r < rows.length; r++) {
        var tds = rows[r].querySelectorAll("td");
        if (!tds.length) continue;
        var bg = dataIndex % 2 === 1 ? SC_ROW_B : SC_ROW_A;
        for (var d = 0; d < tds.length; d++) {
          tds[d].style.setProperty("background-color", bg, "important");
        }
        dataIndex++;
      }

      makeScrollable(table);
    }
  }

  /* ------------------------------------------------------------------------
     Wide charts on a phone: scroll sideways, pin the size column.

     These charts run to four, six, sometimes seven columns, and most of the
     cells are RANGES ("37.5–39.5"). Squeezed into 414px the header cells wrap
     onto two and three lines, the ranges break across the hyphen, and the table
     stops being readable — the buyer's "it really starts to break".

     Scaling the type down instead was the other option and it is a worse one:
     six columns on a phone needs about 8px type to fit, and this shop's
     customer is 50+. Shrinking to fit trades a broken layout for one that
     cannot be read at all, which is not a trade.

     So the table keeps its size and scrolls, and the FIRST column stays pinned,
     because the only thing that makes a size chart usable is knowing which row
     you are reading while you scan across it. When the chart already fits
     (a three or four column one on a wide phone) nothing scrolls and nothing
     here is visible — the affordance only appears when it is needed.

     Done in JS rather than CSS for the same reason as the rest of this
     function: the markup is page content and cannot be edited, and the copy
     theme.js drops into the opened tool-tip carries these inline styles with
     it. `position: sticky` resolves against the nearest scrolling ancestor,
     which is the wrapper added here — note `overflow-x: auto`, never `hidden`,
     which would make it a scroll container that KILLS the sticky inside it.
     ------------------------------------------------------------------------ */
  function makeScrollable(table) {
    var parent = table.parentNode;
    if (!parent || (parent.className || "").indexOf("cl-sizechart-scroll") > -1) return;

    /* Two nested elements, not one. The fade that says "there is more table to
       the right" has to be anchored to something that does NOT scroll — put it
       on the scroller itself and it slides away with the content, ending up
       over the far right of the TABLE instead of over the right of the window
       onto it. So `frame` is the still parent the fade is drawn on, and
       `wrap` is the part that actually moves. */
    var frame = document.createElement("div");
    frame.className = "cl-sizechart-frame";
    frame.style.cssText = "position:relative;max-width:100%;";

    var wrap = document.createElement("div");
    wrap.className = "cl-sizechart-scroll";
    /* inline, so it survives the innerHTML copy into the tool-tip */
    wrap.style.cssText = "overflow-x:auto;overflow-y:hidden;-webkit-overflow-scrolling:touch;max-width:100%;";

    parent.insertBefore(frame, table);
    frame.appendChild(wrap);
    wrap.appendChild(table);

    /* A range must never break across its hyphen, and a header must never
       stack — both are what made the table look broken rather than narrow. */
    var cells = table.querySelectorAll("th, td");
    for (var i = 0; i < cells.length; i++) {
      cells[i].style.setProperty("white-space", "nowrap", "important");
    }

    /* Pin column one. The header cell keeps the ink fill it was given above;
       the body cells keep whichever zebra band they landed on, which is why
       the background is read back off the element rather than assumed — a
       transparent sticky cell lets the scrolling columns slide under it. */
    var rows = table.querySelectorAll("tr");
    for (var r = 0; r < rows.length; r++) {
      var first = rows[r].children[0];
      if (!first) continue;
      var bg = first.style.backgroundColor
        || (first.tagName === "TH" ? SC_HEAD : SC_ROW_A);
      first.style.setProperty("position", "sticky", "important");
      first.style.setProperty("left", "0", "important");
      first.style.setProperty("z-index", "2", "important");
      first.style.setProperty("background-color", bg, "important");
      first.style.setProperty("box-shadow", "1px 0 0 0 " + SC_LINE, "important");
    }
  }

  document.addEventListener("shopify:section:load", restyleSizeCharts);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", restyleSizeCharts);
  } else {
    restyleSizeCharts();
  }

  /* Mark the scrollers that actually overflow, so the fade on the right edge
     only appears where there is more table to reach.

     This cannot be decided when the wrapper is built: at that point the chart
     is still inside .tool-tip-trigger__content, which is display:none, so every
     width reads 0. It has to be measured on the COPY, after the tool-tip opens
     — hence the delayed sweep on any tool-tip trigger click rather than a check
     at load. `is-at-end` is what hides the fade again once she has scrolled all
     the way across; without it the page keeps promising more table than exists. */
  function markScrollers() {
    var wraps = document.querySelectorAll(".cl-sizechart-scroll");
    for (var i = 0; i < wraps.length; i++) {
      (function (w) {
        var frame = w.parentElement;
        if (!frame) return;
        var over = w.scrollWidth - w.clientWidth > 2;
        frame.classList.toggle("is-scrollable", over);
        if (over && !w.dataset.clBound) {
          w.dataset.clBound = "1";
          w.addEventListener("scroll", function () {
            frame.classList.toggle("is-at-end",
              w.scrollLeft + w.clientWidth >= w.scrollWidth - 2);
          }, { passive: true });
        }
      })(wraps[i]);
    }
  }

  document.addEventListener("click", function (e) {
    if (!e.target.closest) return;
    if (!e.target.closest("tool-tip-trigger, [data-tool-tip], .tool-tip-trigger")) return;
    setTimeout(function () { restyleSizeCharts(); markScrollers(); }, 250);
    setTimeout(markScrollers, 900);
  }, true);

  window.addEventListener("resize", markScrollers, { passive: true });

  /* ------------------------------------------------------------------------
     MILESTONE LADDER  (cart drawer + collection sticky bar)

     The bar is built once and then patched. Re-rendering the markup on every
     cart change handed the browser a brand new node already sitting at the new
     width, so `transition: width` had nothing to travel out of and the fill
     jumped straight to the answer instead of closing the gap.
     ------------------------------------------------------------------------ */
  function tiers(el) {
    /* data-cl-tiers="2:10,3:15,4:20" — quantities and percentages, in order */
    var raw = (el.getAttribute("data-cl-tiers") || "2:10,3:15,4:20").split(",");
    return raw.map(function (pair) {
      var bits = pair.split(":");
      return { at: parseInt(bits[0], 10), pct: parseInt(bits[1], 10) };
    }).filter(function (t) { return t.at && t.pct; });
  }

  /* Equal steps: one stop per tier rather than by raw item count. The tiers are
     2/3/4 items, which is not a linear scale — spacing them by count puts them
     all in the first third of the bar. */
  function ladderPct(units, stops) {
    var step = 100 / stops.length;
    if (units >= stops[stops.length - 1]) return 100;
    for (var i = stops.length - 1; i >= 0; i--) {
      if (units >= stops[i]) {
        var frac = (units - stops[i]) / (stops[i + 1] - stops[i]);
        return (i + 1 + frac) * step;
      }
    }
    return (units / stops[0]) * step;
  }

  function replay(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  }

  function paint(root, units) {
    var list = tiers(root);
    var stops = list.map(function (t) { return t.at; });

    var fill = root.querySelector("[data-cl-fill]");
    if (fill) fill.style.width = ladderPct(units, stops) + "%";

    var nodes = root.querySelectorAll("[data-cl-node]");
    for (var i = 0; i < nodes.length; i++) {
      var hit = list[i] && units >= list[i].at;
      var was = nodes[i].classList.contains("is-hit");
      nodes[i].classList.toggle("is-hit", !!hit);
      if (hit && !was) replay(nodes[i], "is-just-hit");
      /* the celebration fills forwards, so it has to come off again when the
         tier is given back — otherwise the dot stays enlarged */
      if (!hit) nodes[i].classList.remove("is-just-hit");
    }

    /* An empty cart makes "Add 1 more for 20% off" untrue — there is no "more"
       yet. The sticky bar hides the line in that state on a phone, where the
       room it costs is the scarcest thing on the page; the cart drawer keeps it,
       because there the ladder IS the point. */
    root.classList.toggle("cl-ladder-empty", !units);

    var msg = root.querySelector("[data-cl-msg]");
    if (msg) {
      var next = null;
      var pct = 0;
      for (var j = 0; j < list.length; j++) {
        if (units >= list[j].at) pct = list[j].pct;
        else if (!next) next = list[j];
      }
      if (!units) {
        msg.innerHTML = "Add <b>" + list[0].at + " items</b> and save <b>" + list[0].pct + "%</b>";
      } else if (next) {
        var away = next.at - units;
        msg.innerHTML = (pct ? "<b>" + pct + "% off</b> unlocked! " : "Great job! ") +
          "Add <b>" + away + " more</b> for <b>" + next.pct + "% off</b>";
      } else {
        msg.innerHTML = "<b>" + pct + "% off</b> unlocked — that's the best we do";
      }
    }

    /* The gift, if the ladder carries one. The move fires on the flip only —
       repeating it on every cart re-render makes it wallpaper. */
    var gift = root.querySelector("[data-cl-gift]");
    if (gift) {
      var at = parseInt(gift.getAttribute("data-cl-gift-at"), 10) || 0;
      var unlocked = at > 0 && units >= at;
      var wasUnlocked = gift.classList.contains("is-unlocked");
      gift.classList.toggle("is-unlocked", unlocked);
      if (unlocked && !wasUnlocked) replay(gift, "is-just-unlocked");
      if (!unlocked) gift.classList.remove("is-just-unlocked");
      var state = gift.querySelector("[data-cl-gift-state]");
      if (state) {
        state.textContent = unlocked
          ? "Added free to your order"
          : "Yours free at " + at + " items";
      }
    }
  }

  function unitsFromCart(cart) {
    return (cart && typeof cart.item_count === "number") ? cart.item_count : 0;
  }

  function paintAll(units) {
    var roots = document.querySelectorAll("[data-cl-ladder]");
    for (var i = 0; i < roots.length; i++) paint(roots[i], units);
  }

  function readCart() {
    return fetch(window.theme && theme.routes ? theme.routes.cart + "?_=" + Date.now() : "/cart.js", {
      credentials: "same-origin",
    }).then(function (r) { return r.json(); });
  }

  function boot() {
    if (!document.querySelector("[data-cl-ladder]")) return;
    readCart().then(function (cart) { paintAll(unitsFromCart(cart)); }).catch(function () {});
  }

  /* The cart drawer works out the KACHING-AWARE count — a bundle line must not
     unlock a tier — and broadcasts it. Prefer that over cart.item_count, which
     counts every line and was putting the sticky bar a tier ahead of the drawer
     for the same bag. If the drawer never runs (no drawer on the page) the
     item_count path below still applies. */
  document.addEventListener("cl:ladder-units", function (e) {
    if (e.detail && typeof e.detail.units === "number") paintAll(e.detail.units);
  });

  /* Impulse broadcasts these after any cart mutation. */
  document.addEventListener("cart:updated", function (e) {
    if (e.detail && e.detail.cart) paintAll(unitsFromCart(e.detail.cart));
    else readCart().then(function (c) { paintAll(unitsFromCart(c)); }).catch(function () {});
  });
  document.addEventListener("cart:build", boot);
  document.addEventListener("shopify:section:load", boot);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* ------------------------------------------------------------------------
     Buy-box review rotation. Six and a half seconds, paused while the pointer
     is on it so a quote can actually be finished.
     ------------------------------------------------------------------------ */
  function initProof() {
    var hosts = document.querySelectorAll("[data-cl-proof]");
    for (var h = 0; h < hosts.length; h++) {
      (function (host) {
        if (host.dataset.clBound) return;
        host.dataset.clBound = "1";
        var items = host.querySelectorAll("[data-cl-proof-item]");
        if (items.length < 2) return;
        var i = 0, timer = null;
        function go() {
          items[i].classList.remove("is-on");
          i = (i + 1) % items.length;
          items[i].classList.add("is-on");
        }
        function play() { stop(); timer = setInterval(go, 6500); }
        function stop() { if (timer) { clearInterval(timer); timer = null; } }
        host.addEventListener("mouseenter", stop);
        host.addEventListener("mouseleave", play);
        play();
      })(hosts[h]);
    }
  }

  document.addEventListener("shopify:section:load", initProof);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initProof);
  } else {
    initProof();
  }

  /* ------------------------------------------------------------------------
     Sticky reward bar reveals once the shopper is actually in the grid.
     ------------------------------------------------------------------------ */
  /* The bar only exists to say how close the bag is to the next tier, so with
     an empty bag it is a promise about nothing — it now stays down until there
     is at least one item. Buyer, 15 Aug.

     `cl:ladder-units` is the drawer's Kaching-aware count (§3) and is the one
     source of truth for "how many items count"; `/cart.js` is only the opening
     read, for the case where the shopper arrives with a bag already filled and
     never touches the cart on this page. */
  var rewardHasItems = false;

  function rewardVisible(bar, inGrid) {
    bar.classList.toggle("is-on", !!(inGrid && rewardHasItems));
  }

  function initReward() {
    var bar = document.querySelector("[data-cl-reward]");
    if (!bar || bar.dataset.clBound) return;
    var target = document.querySelector(bar.getAttribute("data-cl-reward-after") || "#CollectionSection")
      || document.querySelector(".collection-grid, main");
    bar.dataset.clBound = "1";

    var inGrid = false;

    function sync() { rewardVisible(bar, inGrid); }

    if (!target || !("IntersectionObserver" in window)) {
      inGrid = true;
    } else {
      new IntersectionObserver(function (entries) {
        inGrid = entries[0].isIntersecting || entries[0].boundingClientRect.top < 0;
        sync();
      }, { threshold: 0, rootMargin: "-10% 0px -20% 0px" }).observe(target);
    }

    document.addEventListener("cl:ladder-units", function (e) {
      var n = e && e.detail && typeof e.detail.units === "number" ? e.detail.units : null;
      if (n !== null) { rewardHasItems = n > 0; sync(); }
    });

    function readCart() {
      fetch("/cart.js", { credentials: "same-origin" })
        .then(function (r) { return r.json(); })
        .then(function (c) { rewardHasItems = (c.item_count || 0) > 0; sync(); })
        .catch(function () {});
    }
    document.addEventListener("cart:updated", readCart);
    document.addEventListener("cart:xhr-updated", readCart);
    document.addEventListener("ajaxProduct:added", readCart);
    readCart();
    sync();
  }

  document.addEventListener("shopify:section:load", initReward);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initReward);
  } else {
    initReward();
  }

  /* ------------------------------------------------------------------------
     Sticky add to cart. Reveals once the real buy box has scrolled off, mirrors
     its price, and delegates the actual add by clicking the real button — see
     the note at the top of snippets/clarendale-sticky-atc.liquid for why it
     must not post its own form.
     ------------------------------------------------------------------------ */
  var ATC_BTN = 'form[action*="/cart/add"] button[name="add"], ' +
                'form[action*="/cart/add"] input[name="add"], ' +
                'form[action*="/cart/add"] [type="submit"]';
  var ATC_PRICE = '.product-single__price, [data-product-price]';

  function realAddButton() {
    var all = document.querySelectorAll(ATC_BTN);
    for (var i = 0; i < all.length; i++) {
      /* skip the quick-shop modals' buttons — they are in the DOM on collection
         pages and on the PDP's recommendations, and they belong to other products */
      if (!all[i].closest(".modal, .quick-product__modal, [data-cl-satc]")) return all[i];
    }
    return null;
  }

  function syncPrice(bar) {
    var dest = bar.querySelector("[data-cl-satc-price]");
    if (!dest) return;

    /* Compose from BOTH nodes rather than mirroring one.

       This used to copy `document.querySelector(ATC_PRICE).innerHTML`, and
       `[data-product-price]` is the CURRENT price element only — the compare-at
       is a sibling, not a child. So on every sale product the bar showed
       "$54.99" with no struck-through $109.99 next to it: the one figure that
       makes the bar worth having. Buyer caught it on Everdrape, 15 Aug.

       Rebuilding with our own classes also keeps §11's treatment (Caslon red,
       grey strike) instead of inheriting whatever the buy box happens to use. */
    var now = document.querySelector("[data-product-price]")
           || document.querySelector(".product-single__price");
    if (!now) return;

    var html = '<span class="cl-satc__now">' + now.textContent.trim() + "</span>";

    var was = document.querySelector("[data-compare-price]")
           || document.querySelector(".product__price--compare");
    if (was) {
      var wasText = was.textContent.trim();
      /* Impulse leaves the node in place and hides it when there is no sale, so
         a visibility check is what stops "was $0.00" appearing on full price. */
      var shown = was.offsetParent !== null || getComputedStyle(was).display !== "none";
      if (wasText && shown) {
        html += '<span class="cl-satc__was">' + wasText + "</span>";
      }
    }
    dest.innerHTML = html;
  }

  function initStickyAtc() {
    var bar = document.querySelector("[data-cl-satc]");
    if (!bar || bar.dataset.clBound) return;
    var anchor = realAddButton();
    if (!anchor) return;
    bar.dataset.clBound = "1";

    /* The button scrolls back to the buy box; it does not add. On a product with
       a size to choose, adding straight from a bar at the bottom of the page
       either guesses her size or fails silently — both are worse than putting
       her in front of the picker. Buyer call 13 Aug. */
    bar.querySelector("[data-cl-satc-add]").addEventListener("click", function () {
      var target = document.querySelector(".product-single__meta, .product__form, form[action*='/cart/add']")
        || realAddButton();
      if (!target) return;
      var top = window.pageYOffset + target.getBoundingClientRect().top;
      /* clear the sticky header, which would otherwise sit over the picker */
      var header = document.querySelector(".site-header, [data-header-height]");
      if (header) top -= header.getBoundingClientRect().height + 12;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    });

    document.addEventListener("variant:change", function (e) {
      var v = e.detail && e.detail.variant;
      syncPrice(bar);
      var img = bar.querySelector("[data-cl-satc-img]");
      if (img && v && v.featured_image && v.featured_image.src) {
        img.src = v.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, "_120x$1$2");
      }
      var add = bar.querySelector("[data-cl-satc-add]");
      if (add) add.disabled = !!(v && v.available === false);
    });

    /* Deliberately a position check on scroll, not an IntersectionObserver.
       An observer only fires when the intersection ratio *crosses* a threshold,
       so a jump that skips the button entirely — an anchor link, a restored
       scroll position, a programmatic scrollTo — never fires at all and the bar
       stays hidden for the rest of the session. Reading the rect answers the
       question at any scroll position, however you arrived there. */
    var ticking = false;

    function apply() {
      ticking = false;
      var btn = realAddButton();
      if (!btn) return;
      /* on once the real button is fully above the fold; off while any of it is
         still on screen, so the two never compete for the same tap */
      var gone = btn.getBoundingClientRect().bottom < 0;
      bar.classList.toggle("is-on", gone);
      bar.setAttribute("aria-hidden", gone ? "false" : "true");
      document.body.classList.toggle("cl-has-satc", gone);

      /* The bar publishes its own height, and the space reserved at the end of
         the page reads it. Those two used to be independent constants in the
         stylesheet and could not both be right: the bar is 93px with an empty
         bag and 108px once the ladder line appears, so whichever number was
         written down covered the end of the page in the other state. Measured,
         it is correct in both — and stays correct the next time the bar gains
         or loses a row. */
      if (gone) {
        var h = Math.round(bar.getBoundingClientRect().height);
        if (h && h !== lastH) {
          lastH = h;
          document.documentElement.style.setProperty("--cl-satc-h", h + "px");
        }
      }
    }
    var lastH = 0;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    apply();
  }

  document.addEventListener("shopify:section:load", initStickyAtc);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStickyAtc);
  } else {
    initStickyAtc();
  }
})();

/* ==========================================================================
   SHIPPING CARRIER BY VISITOR COUNTRY

   Rewrites every `[data-lc-carrier]` span — printed server-side as "USPS" by
   `snippets/lc-carrier.liquid` — with the postal carrier that will actually
   deliver to the person reading the page.

   The country CANNOT come from Liquid on this shop. `localization.country`
   reports the market being browsed, and there is one market (US), so a request
   from Thailand still reports "US". Shopify's own geolocation endpoint,
   /browsing_context_suggestions.json, returns the IP-detected country in
   `detected_values.country.handle` — that same request gets "TH". It is the
   endpoint behind Shopify's "shop in your country" prompt, so it needs no app
   and no key.

   The answer is cached in sessionStorage: it cannot change mid-session, and
   without the cache every page view spends a request re-asking the same
   question.

   ⚠️ An unmapped country gets "USPS" (buyer, 17 Sep 2026: "if someone is
   visiting from a country we don't ship to, fall back to USPS"). It used to
   empty the span instead; that is what the whitespace note in
   snippets/lc-carrier.liquid was about.
   ========================================================================== */
(function () {
  // National postal operators — the last-mile carrier for this shop's routes.
  // Anything not listed here falls back to no carrier name at all.
  // 17 Sep 2026, buyer: ONLY the countries the checkout ships to (its country
  // dropdown that day: six), everyone anywhere else sees USPS. The 40-country
  // list that stood here named carriers for places the shop cannot deliver to.
  var CARRIERS = {
    US: "USPS",            CA: "Canada Post",     AU: "Australia Post",
    NZ: "NZ Post",         IE: "An Post",         CH: "Swiss Post"
  };

  function paint(code) {
    var name = Object.prototype.hasOwnProperty.call(CARRIERS, code) ? CARRIERS[code] : "USPS";
    var spans = document.querySelectorAll("[data-lc-carrier]");
    for (var i = 0; i < spans.length; i++) {
      if (spans[i].textContent !== name) spans[i].textContent = name;
    }
  }

  var KEY = "lcVisitorCountry";
  var known = null;
  try { known = sessionStorage.getItem(KEY); } catch (e) { /* private mode */ }

  function run() {
    if (known) { paint(known); return; }
    fetch("/browsing_context_suggestions.json", {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var d = j && j.detected_values && j.detected_values.country;
        var code = d && d.handle;
        if (!code) return;                 // leave the server default standing
        known = code;
        try { sessionStorage.setItem(KEY, code); } catch (e) {}
        paint(code);
      })
      .catch(function () { /* offline or blocked — USPS default stands */ });
  }

  // Sections re-render in the theme editor and on some cart/section updates,
  // which brings back the server-printed default; repaint from what we know.
  document.addEventListener("shopify:section:load", function () {
    if (known) paint(known);
  });

  // One repaint after load. Both marquees carrying the carrier today are in
  // `grid` layout, which renders once — but the `marquee` layout CLONES its
  // track to fill wide viewports, and a clone taken before the country came
  // back would carry the server default forever. By load, cloning is done.
  window.addEventListener("load", function () {
    if (known) paint(known);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();
